// Choices players make outside their own actions, mostly when an event is revealed (e.g. "each civilization gains 2
// food or 2 resources"). An event asks for a decision (RequestDecision); the player answers it with a Decide move, at
// any time, even outside their turn. Until they do:
//   - their own turn cannot go on (they decide first);
//   - it holds up the player whose turn it is (every decision blocks), and decisions are made one at a time;
//   - they see the game as it was when the decision was asked (see Authority.ViewFor), so they decide with exactly
//     the knowledge they had then.
// A decision is plain data: { id, player, type, event, prompt, options, blocking, context, ui? }. options lists the
// answers ([{ label, value, section?, cost? }]), or is null when there are too many to list and each answer is checked
// instead (e.g. which 3 cards to discard); ui then names how the client asks for it. Its type (below) says what each
// answer does.
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { CardRef } = require('../Utilities/CardRefs');
const { CivilCardTypes, MilitaryCardTypes } = require('../Constants/Names/CardTypes');
const { Age } = require('../Constants/Constants');

const UrbanTypes = Object.values(TechnologyTypes.Urban);
const { Farm, Mine } = TechnologyTypes.Production;

// The player's cards with a worker on them, of these types (any type when none are given)
const builtCards = (player, types) => player.technologies.filter(tech => tech.GetWorkerCount() > 0 && (!types || types.includes(tech.type)));

// The move rules and colonization, needed lazily (they need this module in turn)
const Moves = () => require('./Moves');
const Colonization = () => require('./Colonization');
const Aggression = () => require('./Aggression');
const War = () => require('./War');
const { WithCardDiscount } = require('./Costs');

// An action that comes with the decision (e.g. Development of Civilization's build), as a move of its own that spends
// no action, with the decision's discounts in place
const innerMove = (decision, value) => ({ ...value, player: decision.player, free: true });
const withDiscounts = (gameState, decision, fn) =>
    WithCardDiscount(gameState.playerStates[decision.player], decision.context.discounts ?? [], fn);

// The answer that takes a player back from a follow-up choice to the one before it (Development of Planning)
const Back = '__back';

// What each kind of decision does with the answer: apply(gameState, decision, value) changes the state and returns a
// line for the move log; validate(gameState, decision, value), when given, checks an answer against the board as it
// is now (the board may have changed since the decision was asked)
const DecisionTypes = {
    // Another action, or nothing; context { discounts }; value: the move (without player) or null
    innerAction: {
        validate: (gameState, decision, value) => value === null ? null
            : withDiscounts(gameState, decision, () => Moves()[value.type].validate(gameState, innerMove(decision, value))),
        apply: (gameState, decision, value) => {
            if (value === null)
            {
                return "does nothing";
            }
            const move = innerMove(decision, value);
            return withDiscounts(gameState, decision, () => {
                const text = Moves()[value.type].describe(gameState, move);
                Moves()[value.type].apply(gameState, move);
                return `: ${text}`;
            });
        },
    },
    // Discard military cards of the player's choice (Politics of Strength); context { count }; value: the card ids
    discardMilitary: {
        validate: (gameState, decision, value) => {
            const hand = gameState.playerStates[decision.player].militaryHand.map(card => card.id);
            const count = Math.min(decision.context.count, hand.length);
            return Array.isArray(value) && value.length === count && new Set(value).size === count && value.every(id => hand.includes(id))
                ? null : `Choose ${count} of your military cards to discard`;
        },
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const discarded = player.militaryHand.filter(card => value.includes(card.id));
            player.militaryHand = player.militaryHand.filter(card => !value.includes(card.id));
            gameState.militaryDiscard.push(...discarded);
            return `discards ${discarded.length} military cards`;
        },
    },
    // Move one worker from one technology card to another, of any kind, paying any difference in cost (Freedom of
    // Movement); value: { from, to } (card names), or null to leave things as they are
    moveWorker: {
        validate: (gameState, decision, value) => value === null ? null : MoveWorkerProblem(gameState.playerStates[decision.player], value),
        apply: (gameState, decision, value) => {
            if (value === null)
            {
                return "moves no worker";
            }
            const player = gameState.playerStates[decision.player];
            const cost = MoveWorkerCost(player, value);
            player.PayResources(cost);
            player.GetTechnology(value.from).AddContent("workers", -1);
            player.GetTechnology(value.to).AddContent("workers");
            // (as the cards; what it cost shows in the changes)
            const card = (name) => CardRef({ type: CivilCardTypes.Technology, name, age: player.GetTechnology(name).age });
            return `moves a worker: ${card(value.from)} → ${card(value.to)}`;
        },
    },
    // International Agreement, step 1: take cards (giving up the next political action), or not; value: true | false
    agreement: {
        apply: (gameState, decision, value) => {
            if (!value)
            {
                return "declines";
            }
            const player = gameState.playerStates[decision.player];
            player.skipNextPolitical = true;
            AskAgreementTake(gameState, player, 5);
            return "accepts, giving up its next political action";
        },
    },
    // International Agreement: take a card from the card row within what is left of the 5 civil actions (the first one
    // gives up the next political phase), be done, or (nothing taken yet) decline; context { budget }; value: { cardId },
    // 'done' or 'decline'. When done, the card row is replenished; done with nothing taken is declining.
    agreementTake: {
        validate: (gameState, decision, value) => {
            if (value === 'done')
            {
                return null;
            }
            if (value === 'decline')
            {
                return decision.context.budget === 5 ? null : "Cards have been taken: put them back to decline";
            }
            const move = { type: 'TakeCard', player: decision.player, cardId: value?.cardId, free: true };
            const problem = Moves()['TakeCard'].validate(gameState, move);
            if (problem)
            {
                return problem;
            }
            const cost = TakeCost(gameState, decision.player, value.cardId);
            return cost > decision.context.budget ? `Needs ${cost} civil actions; ${decision.context.budget} left` : null;
        },
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const first = decision.context.budget === 5;
            if (value === 'decline' || (value === 'done' && first))
            {
                return "declines";
            }
            if (value === 'done')
            {
                Moves().ReplenishCardRow(gameState);
                return "is done taking cards; the card row is replenished";
            }
            if (first)
            {
                player.skipNextPolitical = true;
            }
            const cost = TakeCost(gameState, decision.player, value.cardId);
            const name = gameState.civilCardRow.find(card => card?.id === value.cardId)?.name;
            Moves()['TakeCard'].apply(gameState, { type: 'TakeCard', player: decision.player, cardId: value.cardId, free: true });
            // Not taken in an action phase: an action card taken this way can be played this turn (when it is the taker's)
            // or on their next one; only cards taken during the action phase must wait
            player.takenThisTurn = player.takenThisTurn.filter(id => id !== value.cardId);
            AskAgreementTake(gameState, player, decision.context.budget - cost);
            return `takes ${name} (${cost} civil action${cost === 1 ? '' : 's'})${first ? ', giving up its next political phase' : ''}`;
        },
    },
    // Defend against an aggression with military cards (up to the military actions, not spent); value: the card ids
    // (none: no defense)
    defend: {
        validate: (gameState, decision, value) =>
            Aggression().DefenseProblem(gameState.playerStates[decision.player], value, decision.context.limit, decision.context.needed),
        apply: (gameState, decision, value) => Aggression().Defend(gameState, decision, value),
    },
    // What a successful aggression does, the aggressor's choice; value: as the aggression's options give it
    aggressionChoice: {
        // (the journal says only what came of it: "Raid: Andrew destroys Matt's Philosophy...", the name said once)
        tellsItself: true,
        apply: (gameState, decision, value) => Aggression().Choose(gameState, value)
            .replace(new RegExp(`^${gameState.playerStates[decision.player].name} `), ''),
    },
    // The victor of War Over Technology: the special technologies to take; value: their names (the rest is science)
    warChoice: {
        validate: (gameState, decision, value) => War().WarChoiceProblem(gameState, decision, value),
        apply: (gameState, decision, value) => War().WarChoice(gameState, decision, value),
    },
    // Answering a pact offered (see Pact.js); value: 'accept' / 'decline'
    pactOffer: {
        apply: (gameState, decision, value) => require('./Pact').AnswerOffer(gameState, value === 'accept'),
    },
    // A blue token owed while the blue bank is empty comes off a farm or mine, the player's choice; value: the card's
    // name. Another is asked for while more are owed (see SettleBlueBank).
    loseBlueToken: {
        validate: (gameState, decision, value) => {
            const tech = gameState.playerStates[decision.player].GetTechnology(value);
            return tech && tech.GetBlueCubeCount() > 0 ? null : "Choose a farm or mine with a token on it";
        },
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const tech = player.GetTechnology(value);
            // Owed no longer (a token came back to the bank since): nothing to give up
            if (player.blueBank >= 0)
            {
                return "owes no blue token any more";
            }
            tech.AddContent("blueCubes", -1);
            SettleBlueBank(gameState, player, decision.event);
            return `gives up a blue token from ${value} (${TokenWorth(tech)})`;
        },
    },
    // Bid for a territory, or pass for good; value: { bid } or { pass: true } (see Colonization.js)
    // (ownEntry: what apply returns is the journal entry itself, a line each, the first its headline; see Decide)
    colonyBid: {
        ownEntry: true,
        validate: (gameState, decision, value) => {
            if (value?.pass === true)
            {
                return null;
            }
            const { MostPayable } = Colonization();
            const highest = gameState.auction?.highest?.bid ?? 0;
            const most = MostPayable(gameState.playerStates[decision.player]);
            if (!Number.isInteger(value?.bid) || value.bid <= highest)
            {
                return `Bid more than ${highest}, or pass`;
            }
            return value.bid > most ? `You could pay at most ${most}` : null;
        },
        apply: (gameState, decision, value) => {
            const auction = gameState.auction;
            // (auctions saved before bids were kept start their list now)
            auction.bids = [...(auction.bids ?? []), value.pass ? { player: decision.player, pass: true } : { player: decision.player, bid: value.bid }];
            if (value.pass)
            {
                auction.passed.push(decision.player);
            } else {
                auction.highest = { player: decision.player, bid: value.bid };
            }
            // How the auction ends, if it does now: the one bidder left wins, or with no bids no one does
            const { highest, territory } = auction;
            const active = auction.order.filter(player => !auction.passed.includes(player));
            const outcome = highest && active.length === 1
                ? `\n${gameState.playerStates[highest.player].name} wins ${CardRef(territory)} with a bid of ${highest.bid}`
                : active.length === 0 ? `\nNo one colonizes ${CardRef(territory)}` : '';
            Colonization().AdvanceAuction(gameState, decision.player);
            return (value.pass ? `Passed on ${CardRef(territory)}` : `Bid ${value.bid} for ${CardRef(territory)}`) + outcome;
        },
    },
    // Pay for the territory won; value: { units: { cardName: count }, bonusCards: [ids], cookCards: [ids], isabella: resources }
    // The journal entry: what was sacrificed and what the player's cards added, a line each, the total, and what the
    // colony gave, e.g. "Colonized Wealthy Territory\nSacrificed 2 x Warrior: 2\nCartography: +2\nTotal: 4 (bid 3)\n+6 resources"
    colonyPay: {
        ownEntry: true,
        validate: (gameState, decision, value) =>
            Colonization().PaymentProblem(gameState.playerStates[decision.player], value, decision.context.bid),
        apply: (gameState, decision, value) => {
            const { PaymentBreakdown, AuctionTerritory, Colonize } = Colonization();
            const player = gameState.playerStates[decision.player];
            const payment = PaymentBreakdown(player, value);
            // The sacrificed units' workers go back to the yellow bank; the cards played are discarded
            Object.entries(value.units).filter(([, count]) => count > 0)
                .forEach(([name, count]) => player.GetTechnology(name).AddContent("workers", -count));
            const played = [...(value.bonusCards ?? []), ...(value.cookCards ?? [])];
            gameState.militaryDiscard.push(...player.militaryHand.filter(card => played.includes(card.id)));
            player.militaryHand = player.militaryHand.filter(card => !played.includes(card.id));
            // (Isabella of Castile: resources for colonization points)
            if (value.isabella > 0)
            {
                player.PayResources(value.isabella);
            }
            const territory = AuctionTerritory(gameState);
            gameState.auction = null;
            const gained = Colonize(gameState, player, territory);
            return [`Colonized ${CardRef(territory)}`,
                ...payment.sacrificed.map(part => part.kind === 'tactic'
                    ? `${part.label} armies: +${part.value}` : `Sacrificed ${part.count} x ${part.label}: ${part.value}`),
                ...payment.bonuses.map(part => `${part.label}: +${part.value}`),
                `Total: ${payment.total} (bid ${decision.context.bid})`,
                ...(gained ? [gained[0].toUpperCase() + gained.slice(1)] : [])].join('\n');
        },
    },
    // Gain one of several amounts (Autonomous Territory); value: { what, amount }
    gainAmount: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            if (value.what === 'food') player.GainFood(value.amount);
            if (value.what === 'resources') player.GainResources(value.amount);
            if (value.what === 'science') player.science += value.amount;
            if (value.what === 'culture') player.culture += value.amount;
            return `+${value.amount} ${value.what}`;
        },
    },
    // Lose one of the player's colonies (it leaves the game); value: its card id
    loseColony: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const colony = player.colonies.find(card => card.id === value);
            player.colonies = player.colonies.filter(card => card.id !== value);
            return `loses ${colony ? CardRef(colony) : 'a colony'}`;
        },
    },
    // Development of Planning: apply one of the drawn Age A events to the player's civilization alone; value: its name
    planning: {
        apply: (gameState, decision, value) => {
            const { EventEffects } = require('./EventEffects');
            const player = gameState.playerStates[decision.player];
            const before = gameState.decisions.length;
            gameState.eventScope = [player];
            let text;
            try {
                text = EventEffects[value](gameState);
            } finally {
                delete gameState.eventScope;
            }
            // A follow-up choice can still go back to picking another of the drawn events
            // It is part of the Planning choice: it takes that choice's place in the order and blocks as it does (even for an
            // event whose own choices do not block, e.g. Development of Markets), so the player resolves their event fully
            // before the next player chooses
            gameState.decisions.slice(before).filter(d => d.player === decision.player).forEach(followUp => {
                followUp.back = { ...decision };
                followUp.order = Order(decision);
                followUp.blocking = decision.blocking;
                followUp.options = [...followUp.options, { label: "← Choose another event", value: Back }];
            });
            // (what it did shows in the changes)
            return `chose ${CardRef({ type: MilitaryCardTypes.Event, name: value, age: Age.AgeA })}`;
        },
    },
    // Gain an amount of one of the things offered; context { amount }; value: 'food' | 'resources' | 'science'
    gainOne: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const amount = decision.context.amount;
            if (value === 'food') player.GainFood(amount);
            if (value === 'resources') player.GainResources(amount);
            if (value === 'science') player.science += amount;
            return `+${amount} ${value}`;
        },
    },
// Steve Jobs, after developing: pay 1 resource for 4 culture (value true), or not
    jobsCulture: {
        tellsItself: true,
        validate: (gameState, decision, value) => value && gameState.playerStates[decision.player].GetResources() < 1 ? "You have no resource to pay" : null,
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            if (!value)
            {
                return "does not pay for culture";
            }
            player.PayResources(1);
            player.culture += 4;
            return "pays 1 resource: +4 culture";
        },
    },
// Himeji Castle in a war being resolved: the unit added (its name), or none (null); once both sides with it have
    // answered, the war is resolved
    himejiWar: {
        validate: (gameState, decision, value) => value === null ? null
            : require('./Aggression').HimejiUnits(gameState.playerStates[decision.player]).some(unit => unit.name === value) ? null : "Choose one of your units",
        apply: (gameState, decision, value) => {
            const war = gameState.wars.find(w => w.card.id === decision.context.warCard);
            war.himeji.units[decision.player] = value;
            if (!gameState.decisions.some(d => d.type === 'himejiWar' && d.context.warCard === war.card.id))
            {
                require('./War').ResolveWar(gameState, war);
            }
            return value === null ? "adds no unit (Himeji Castle)" : `adds ${value} (Himeji Castle)`;
        },
    },
// Suez Canal, just completed: a territory from the hand colonized (its id), or none (null)
    suezColonize: {
        tellsItself: true,
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const territory = player.militaryHand.find(c => c.id === value);
            if (!territory)
            {
                return "colonizes nothing";
            }
            player.militaryHand = player.militaryHand.filter(c => c !== territory);
            const gained = Colonization().Colonize(gameState, player, territory);
            return `colonizes ${require('../Utilities/CardRefs').CardRef(territory)}${gained ? `: ${gained}` : ''}`;
        },
    },
// United Nations, just completed: an Age III event from the hand revealed and evaluated now (its id), or none (null);
    // it goes back to the hand, seen by everyone. Revealing a card: sent at once.
    unEvent: {
        reveals: true,
        tellsItself: true,
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const event = player.militaryHand.find(c => c.id === value);
            if (!event)
            {
                return "reveals no event";
            }
            const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
            const before = GetAllStats(gameState);
            const text = require('./EventEffects').ResolveEvent(gameState, event);
            gameState.reports.push({ name: event.name, age: event.age, card: require('../Utilities/Converters/CardToData')(event), revealedBy: player.name,
                round: gameState.round, text: `(United Nations; back in ${player.name}'s hand) ${text}`, changes: PlayerChanges(before, GetAllStats(gameState)),
                revealCards: [event.id] });
            return `reveals ${require('../Utilities/CardRefs').CardRef(event)}: ${text}`;
        },
    },
    // Ian Fleming, at the start of the political phase: whose military cards to look at (a player number), or no one
    // (null). The cards are shown by the server, so the answer is sent at once.
    flemingPeek: {
        reveals: true,
        tellsItself: true,
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            player.CardState(decision.event).lastTarget = value;
            return value === null ? "looks at no one's military cards" : `looks at ${gameState.playerStates[value].name}'s military cards`;
        },
    },
    // Gain (or lose) food and resources in a split; value: { food, resources }
    split: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            const sign = decision.context.lose ? -1 : 1;
            if (sign > 0)
            {
                player.GainFood(value.food);
                player.GainResources(value.resources);
            } else {
                player.LoseFood(value.food);
                player.LoseResources(value.resources);
            }
            const verb = sign > 0 ? '+' : '−';
            return [value.food ? `${verb}${value.food} food` : null, value.resources ? `${verb}${value.resources} resources` : null]
                .filter(part => part).join(', ') || 'nothing';
        },
    },
    // Build on a card for free with an unused worker, or not; context { techName }; value: true | false
    freeBuild: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            if (!value)
            {
                return `does not build`;
            }
            player.GetTechnology(decision.context.techName).AddContent("workers");
            player.workerPool--;
            return `builds ${decision.context.techName} for free`;
        },
    },
    // Destroy a building or unit of a player (the chooser's own, or context.target's); value: the card's name.
    // The worker goes back to that player's worker pool.
    destroy: {
        // (the journal says only what was destroyed)
        tellsItself: true,
        apply: (gameState, decision, value) => {
            const target = gameState.playerStates[decision.context.target ?? decision.player];
            target.GetTechnology(value).AddContent("workers", -1);
            target.workerPool++;
            const ref = require('../Utilities/CardRefs').CardRef({ type: 'Technology', name: value, age: target.GetTechnology(value)?.age });
            return decision.context.target !== undefined ? `destroys ${target.name}'s ${ref}` : `destroys ${ref}`;
        },
    },
    // Lose a worker from a card (none is unused); value: the card's name. It goes back to the yellow bank.
    loseWorker: {
        apply: (gameState, decision, value) => {
            gameState.playerStates[decision.player].GetTechnology(value).AddContent("workers", -1);
            return `loses a worker from ${value}`;
        },
    },
    // Ravages of Time: turn a wonder face down; value: the wonder's card id
    crumble: {
        apply: (gameState, decision, value) => {
            const player = gameState.playerStates[decision.player];
            player.crumbledWonders.push(value);
            return `${player.wonders.find(wonder => wonder.id === value)?.name} crumbles`;
        },
    },
    // International Negotiations: the strongest takes 3 of the chosen thing from the weakest; context { strongest, weakest }
    negotiate: {
        apply: (gameState, decision, value) => {
            const strong = gameState.playerStates[decision.context.strongest];
            const weak = gameState.playerStates[decision.context.weakest];
            if (value === 'food') { weak.LoseFood(3); strong.GainFood(3); }
            if (value === 'resources') { weak.LoseResources(3); strong.GainResources(3); }
            if (value === 'science') { weak.science -= 3; strong.science += 3; }
            return `${strong.name} takes 3 ${value} from ${weak.name}`;
        },
    },
};

// Every decision blocks: the turn waits for it, and they are made one at a time, as at the table. (Development of
// Markets, Foray and Raiders once let the turn go on without waiting, but a game then told its choices out of order.)

// Blocking decisions are made one at a time, in the order they were asked (which is turn order for an event that asks
// everyone): the oldest open one, or undefined
// (order: a follow-up or a decision asked again keeps the place of the one it comes from)
const Order = (decision) => decision.order ?? decision.id;
const ByOrder = (a, b) => Order(a) - Order(b) || a.id - b.id;
const ActiveBlocking = (gameState) => gameState.decisions.filter(d => d.blocking).sort(ByOrder)[0];
// Why this decision cannot be made yet (an earlier blocking one is still open), or null
const DecisionOrderProblem = (gameState, decision) => {
    const active = ActiveBlocking(gameState);
    return decision.blocking && active && active.id !== decision.id
        ? `Waiting for ${gameState.playerStates[active.player].name} to decide on ${active.event} first` : null;
};
// Decisions whose answers can depend on earlier answers to the same event, by refresh key: their options are worked
// out again when their turn comes (e.g. Development of Civilization with Scientific Cooperation: a pact-mate's
// discovery takes science from the other)
const Refreshers = {
    civilization: (gameState, player) => require('./EventEffects').CivilizationOptions(gameState, player),
};

// Asks a player for a decision. With no possible answer nothing happens; with one, it is made straight away; otherwise
// it waits for the player. Returns the log line of a decision made straight away, or null.
// spec: { player (a PlayerState), type, event, prompt, options, blocking? (default: true), context? }
const RequestDecision = (gameState, spec) => {
    const decision = {
        player: spec.player.playerNumber,
        type: spec.type,
        event: spec.event,
        prompt: spec.prompt,
        options: spec.options,
        blocking: spec.blocking ?? true,
        context: spec.context ?? {},
        ...(spec.ui ? { ui: spec.ui } : {}),
        ...(spec.refresh ? { refresh: spec.refresh } : {}),
    };
    if (decision.options !== null && decision.options.length === 0)
    {
        return null;
    }
    // (Not for an event applied to one player by Development of Planning: they may still go back to pick another)
    if (decision.options !== null && decision.options.length === 1 && !gameState.eventScope)
    {
        return `${spec.player.name} ${DecisionTypes[decision.type].apply(gameState, decision, decision.options[0].value)}`;
    }
    gameState.decisions.push({ id: gameState.nextDecisionId++, ...decision });
    return null;
};

// Freedom of Movement: what moving a worker between two cards costs (the difference, if the new card costs more,
// after discounts on either card), and why a move breaks the rules, if it does
// How that cost comes about, to show it: each card's cost to build after the changes to it (e.g. Masonry's discount on
// urban buildings, a leader's), with those changes; changes to the payment itself (e.g. an action card's); and the
// total: { from: { name, printed, cost, adjustments }, to: { ... }, payment: [adjustments], total }
// One card's cost to build after the changes to it: { name, printed, cost, adjustments }
const CardBuildCost = (player, name) => {
    const { BuildCostOf } = require('./Costs');
    const tech = player.GetTechnology(name);
    const cost = BuildCostOf(player, tech);
    return { name, printed: tech.workerCost, cost: cost.total, adjustments: cost.adjustments.map(a => ({ source: a.source, amount: a.amount })) };
};
const MoveWorkerCostDetails = (player, { from, to }) => {
    const { GetUpgradeCost, PaymentAdjustments } = require('./Costs');
    const card = (name) => CardBuildCost(player, name);
    const toTech = player.GetTechnology(to);
    return { from: card(from), to: card(to),
        payment: PaymentAdjustments(player, toTech).map(a => ({ source: a.source, amount: a.amount })),
        total: GetUpgradeCost(player, player.GetTechnology(from), toTech, true).total };
};
const MoveWorkerCost = (player, { from, to }) => {
    const { GetUpgradeCost } = require('./Costs');
    const fromTech = player.GetTechnology(from);
    const toTech = player.GetTechnology(to);
    return GetUpgradeCost(player, fromTech, toTech, true).total;
};
const MoveWorkerProblem = (player, value) => {
    const from = player.GetTechnology(value?.from);
    const to = player.GetTechnology(value?.to);
    const buildable = (tech) => tech && tech.workerCost !== null && tech.workerCost !== undefined;
    if (!buildable(from) || from.GetWorkerCount() < 1)
    {
        return "Choose a card with a worker on it to move from";
    }
    if (!buildable(to) || to === from)
    {
        return "Choose another card to move the worker to";
    }
    if (UrbanTypes.includes(to.type) && to.type !== from.type)
    {
        const built = player.technologies.filter(t => t.type === to.type).reduce((sum, t) => sum + t.GetWorkerCount(), 0);
        if (built >= player.GetUrbanBuildingLimit())
        {
            return `Your government allows only ${player.GetUrbanBuildingLimit()} ${to.type} buildings`;
        }
    }
    const cost = MoveWorkerCost(player, value);
    return player.GetResources() < cost ? `Needs ${cost} resources` : null;
};

// International Agreement: the civil actions a card in the row would cost the player (with their usual adjustments)
const TakeCost = (gameState, playerNumber, cardId) => {
    const { GetTakeCardCost } = require('./Costs');
    const slot = gameState.civilCardRow.findIndex(card => card?.id === cardId);
    return GetTakeCardCost(gameState.playerStates[playerNumber], gameState.civilCardRow[slot], slot);
};

// International Agreement: asks the player for the next card to take, while there is something left to take; when
// nothing is, the row is replenished straight away
// (Asked even when nothing more can be taken: the player says when they are done, and can still change their mind)
// budget: the civil actions left to take cards with (5 to start with: nothing taken yet, so it can still be declined)
const AskAgreementTake = (gameState, player, budget) => {
    RequestDecision(gameState, {
        player, type: 'agreementTake', event: 'International Agreement', blocking: true, ui: 'agreementTake',
        prompt: budget === 5
            ? "You may take 5 CA worth of cards at the cost of your next political phase."
            : `You may take ${budget} CA more worth of cards.`,
        options: null, context: { budget },
    });
};

// What one blue token on a farm or mine is worth, e.g. "1 food", "2 resources"
// (a lab used as a mine, Bill Gates: its level)
const TokenWorth = (tech) => {
    const worth = tech.type === 'Lab' ? tech.resourceValue : tech.multiplier;
    return `${worth} ${tech.type === 'Farm' ? 'food' : worth === 1 ? 'resource' : 'resources'}`;
};

// While the player owes blue tokens (their bank is below 0: an event or a colony took one away, or a wonder stage was
// covered, with the bank empty), asks them which farm or mine to take the next one from (made at once when there is only one choice).
// With no token on any farm or mine there is nothing to give up.
// blocking: the turn waits for the answer (owed from a turn that is over: a stage of the player's Red Cross built by
// someone else)
const SettleBlueBank = (gameState, player, source, blocking = false) => {
    if (player.blueBank >= 0 || gameState.decisions.some(d => d.player === player.playerNumber && d.type === 'loseBlueToken'))
    {
        return null;
    }
    // (a lab used as a mine, Bill Gates, holds them too)
    const cards = player.technologies.filter(tech => (tech.type === 'Farm' || tech.type === 'Mine' || tech.type === 'Lab') && tech.GetBlueCubeCount() > 0);
    return RequestDecision(gameState, {
        player, type: 'loseBlueToken', event: source, ...(blocking ? { blocking: true } : {}),
        prompt: "Your blue bank is empty: choose the farm or mine to take a blue token from",
        options: cards.map(tech => ({ label: `${TokenWorth(tech)} from ${tech.name}`, value: tech.name })),
    });
};

// Applies a player's answer to a decision and returns the log line. Going back (see Back) asks the earlier decision again.
const ResolveDecision = (gameState, decision, value) => {
    gameState.decisions = gameState.decisions.filter(other => other.id !== decision.id);
    if (value === Back && decision.back)
    {
        gameState.decisions.push({ ...decision.back, id: gameState.nextDecisionId++, order: Order(decision.back) });
        return "goes back to choose again";
    }
    const text = DecisionTypes[decision.type].apply(gameState, decision, value);
    RefreshNext(gameState);
    return text;
};

// The next blocking decision, now that the ones before it are made: options that depend on earlier answers are worked
// out again; with one left it is made straight away, with none it is dropped (and so on down the line)
const RefreshNext = (gameState) => {
    for (;;)
    {
        const next = ActiveBlocking(gameState);
        if (!next?.refresh || next.refreshed)
        {
            return;
        }
        const player = gameState.playerStates[next.player];
        next.options = Refreshers[next.refresh](gameState, player);
        next.refreshed = true;
        // A choice that can go back (Development of Planning) keeps that option, and stays to be made
        if (next.back)
        {
            next.options.push({ label: "← Choose another event", value: Back });
            return;
        }
        if (next.options.length > 1)
        {
            return;
        }
        gameState.decisions = gameState.decisions.filter(other => other.id !== next.id);
        if (next.options.length === 1)
        {
            const { LogTrigger } = require('../Effects/Effects');
            LogTrigger(player, next.event, `${player.name} ${DecisionTypes[next.type].apply(gameState, next, next.options[0].value)}`);
        }
    }
};

// The decision the player has to make first (their oldest), or undefined
// The card a decision is about (decision.event names it), as a card when it is in sight: the pact offered, the
// aggression, a war, the territory up for colonization, the last event revealed, or a leader or wonder in play;
// otherwise its name
const DecisionSubject = (gameState, decision) => {
    const inPlay = gameState.playerStates.flatMap(player => [player.leader, ...player.wonders, player.wonderInProgress?.card]);
    const card = [gameState.pactOffer?.card, gameState.aggression?.card, ...gameState.wars.map(war => war.card),
        gameState.auction?.territory, gameState.lastEvent?.card, ...inPlay].find(c => c && c.name === decision.event);
    return card ? CardRef(card) : decision.event;
};

const NextDecisionFor = (gameState, playerNumber) => gameState.decisions
    .filter(decision => decision.player === playerNumber)
    .sort(ByOrder)[0];

// Answers are compared as data (an answer can be an object, e.g. a split)
const SameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

module.exports = {
    AskAgreementTake,
    DecisionTypes,
    RequestDecision,
    ResolveDecision,
    RefreshNext,
    NextDecisionFor,
    DecisionSubject,
    ActiveBlocking,
    DecisionOrderProblem,
    ByOrder,
    SameValue,
    Back,
    builtCards,
    MoveWorkerCost,
    MoveWorkerCostDetails,
    CardBuildCost,
    MoveWorkerProblem,
    SettleBlueBank,
    UrbanTypes,
    FarmsAndMines: [Farm, Mine],
};
